import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import ActiviteEntente from '../../../../../../../common/src/entities/activite-entente.entity';
import { Entente } from '../../../../../../../common/src/entities';

const styles = StyleSheet.create({
    table: { width: '100%', margin: 2 },
    tableRow: { flexDirection: 'row', paddingBottom: 1 },
    tableHeader: { fontWeight: 'bold', backgroundColor: 'black', color: 'white', padding: 2 },
    tableCell: { flex: 1, textAlign: 'center', padding: 1, fontSize: 9 },
    totalRow: { fontWeight: 'bold', backgroundColor: 'black', color: 'white', padding: 1 },
});

const formatDate = (date: string) => {
    const [year, month, day] = date.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
};

export default function DetailsActiviteEntentePDF({
    activiteEntente,
    entente,
    activiteEntenteBus = [],
    type_visibilite,
    afficherDateActivite = true,
    afficherContexte = true,
    afficherParticipants = true,
    afficherEtendueAge = true,
    afficherTypeActivite = true,
    afficherUrgence = true,
    afficherTypeClient = true,
    afficherLangue = true,
    afficherLieu = true,
    afficherAutresDetails = true,
    afficherPolitiquesAutobus = true,
    afficherFactureRelated = false,
}: {
    activiteEntente: ActiviteEntente;
    entente: Entente;
    activiteEntenteBus: ActiviteEntente[];
    type_visibilite: 'client' | 'fournisseur';
    afficherDateActivite?: boolean;
    afficherContexte?: boolean;
    afficherParticipants?: boolean;
    afficherEtendueAge?: boolean;
    afficherTypeActivite?: boolean;
    afficherUrgence?: boolean;
    afficherTypeClient?: boolean;
    afficherLangue?: boolean;
    afficherLieu?: boolean;
    afficherAutresDetails?: boolean;
    afficherPolitiquesAutobus?: boolean;
    afficherFactureRelated?: boolean;
}) {
    const hasAutobus = activiteEntenteBus.length > 0;

    let notes = '';

    if (type_visibilite === 'client') {
        notes +=
            'Notes du client\n' +
            activiteEntente.activite.notes +
            '\n' +
            activiteEntente.activite.notes_client +
            '\n\n';
    } else if (type_visibilite === 'fournisseur') {
        notes +=
            'Notes du fournisseur\n' +
            activiteEntente.activite.notes_fournisseur +
            '\n' +
            activiteEntente.activite.fournisseur.notes +
            '\n\n';
    }

    if (activiteEntente.activiteEntenteProduit.length > 0) {
        notes += 'Notes du produit\n';

        activiteEntente.activiteEntenteProduit.forEach((produit) => {
            if (!produit.note_client_invisible && type_visibilite === 'client')
                notes += produit.produit.notes_client + '\n';

            if (!produit.note_fournisseur_invisible && type_visibilite === 'fournisseur')
                notes += produit.produit.notes_fournisseur + '\n';

            if (!produit.note_interne_invisible) notes += produit.produit.notes_internes + '\n';
        });

        notes += '\n';
    }

    if (entente.notes.length > 0) {
        notes += 'Autres notes\n';

        entente.notes.forEach((note) => {
            if (
                (note.pour_quoi.includes('client') && type_visibilite === 'client') ||
                (note.pour_quoi.includes('fournisseur') && type_visibilite === 'fournisseur')
            )
                notes += note.name + '\n';
        });
    }

    return (
        <>
            <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                        Détails de l'activité
                    </Text>
                </View>

                {afficherDateActivite && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                            Date d'activité
                        </Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {entente.date_activite ? formatDate(entente.date_activite) : 'NA'}
                        </Text>
                    </View>
                )}

                {afficherContexte && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>Contexte</Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {entente.contexte ?? 'NA'}
                        </Text>
                    </View>
                )}

                {afficherParticipants && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>Participants</Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>0</Text>
                    </View>
                )}

                {afficherEtendueAge && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>Étendue d'âge</Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {entente.groupe_age ?? 'NA'}
                        </Text>
                    </View>
                )}

                {afficherTypeActivite && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                            Type d'activité
                        </Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {activiteEntente?.activite?.typeActivite?.name ?? 'NA'}
                        </Text>
                    </View>
                )}

                {afficherUrgence && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                            En cas d'urgence
                        </Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            Communiquez avec Isaac au 514-942-5155
                        </Text>
                    </View>
                )}

                {afficherTypeClient && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                            Type de client
                        </Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {entente.client.prefix ?? 'NA'}
                        </Text>
                    </View>
                )}

                {afficherLangue && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>Langue</Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {activiteEntente.activite.langues?.join(', ') ?? 'Français'}
                        </Text>
                    </View>
                )}

                {afficherLieu && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                            L'activité a lieu
                        </Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {entente.lieu.adresse ?? 'NA'}
                        </Text>
                    </View>
                )}

                {afficherAutresDetails && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                            Autres détails
                        </Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>{notes}</Text>
                    </View>
                )}

                {afficherPolitiquesAutobus && hasAutobus && (
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                            Politiques autobus
                        </Text>
                        <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                            {activiteEntenteBus.map((item) => item.activite.guide_clauses_politiques).join('\n')}
                        </Text>
                    </View>
                )}

                {afficherFactureRelated && (
                    <>
                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                                Minimum facturable
                            </Text>
                            <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                                Le dernier nombre ou forfait confirmé
                            </Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { textAlign: 'left', fontWeight: 'bold' }]}>
                                Sondage qualité
                            </Text>
                            <Text style={[styles.tableCell, { flex: 4, textAlign: 'left' }]}>
                                Faites nous part de vos commentaires au lien suivant: insérer lien
                            </Text>
                        </View>
                    </>
                )}
            </View>
        </>
    );
}
